import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import { adminManagementApi } from '../services/api';
import { colors, badgeStyles, utilityStyles } from '../styles/sharedStyles';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAdmins, setTotalAdmins] = useState(0);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'membership_admin',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch admins
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminManagementApi.getAll({
        page: currentPage,
        limit: 10,
        search: searchQuery,
      });
      setAdmins(response.data.admins);
      setTotalPages(response.data.totalPages);
      setTotalAdmins(response.data.totalAdmins);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load admins. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [currentPage, searchQuery]);

  // Handle search
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Open add admin modal
  const handleAddAdmin = () => {
    setModalMode('add');
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'membership_admin',
      isActive: true,
    });
    setFormErrors({});
    setSelectedAdmin(null);
    setIsModalOpen(true);
  };

  // Open edit admin modal
  const handleEditAdmin = (admin) => {
    setModalMode('edit');
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      role: admin.role,
      isActive: admin.isActive,
    });
    setFormErrors({});
    setSelectedAdmin(admin);
    setIsModalOpen(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    // Password is optional for new admins (will be auto-generated)
    if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    return errors;
  };

  // Submit form
  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      if (modalMode === 'add') {
        await adminManagementApi.create(formData);
      } else {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send password if not changing
        }
        await adminManagementApi.update(selectedAdmin.id, updateData);
      }
      setIsModalOpen(false);
      fetchAdmins();
    } catch (err) {
      console.error('Error saving admin:', err);
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to save admin. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete admin
  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Are you sure you want to delete ${admin.name}?`)) {
      return;
    }

    try {
      await adminManagementApi.delete(admin.id);
      fetchAdmins();
    } catch (err) {
      console.error('Error deleting admin:', err);
      alert(err.response?.data?.message || 'Failed to delete admin');
    }
  };

  // Table columns
  const columns = [
    {
      header: 'Name',
      accessor: 'name',
    },
    {
      header: 'Email',
      accessor: 'email',
    },
    {
      header: 'Role',
      accessor: 'role',
      render: (role) => {
        const roleLabels = {
          super_admin: 'Super Admin',
          membership_admin: 'Membership Admin',
          content_admin: 'Content Admin',
        };
        return (
          <span
            style={{
              ...badgeStyles.base,
              ...(role === 'super_admin' ? badgeStyles.success : badgeStyles.info),
            }}
          >
            {roleLabels[role] || role}
          </span>
        );
      },
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (isActive) => (
        <span
          style={{
            ...badgeStyles.base,
            ...(isActive ? badgeStyles.success : badgeStyles.danger),
          }}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Last Login',
      accessor: 'lastLogin',
      render: (lastLogin) =>
        lastLogin ? new Date(lastLogin).toLocaleDateString() : 'Never',
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (id, admin) => (
        <div style={{ ...utilityStyles.flexRow, ...utilityStyles.gap8 }}>
          <Button
            size="small"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleEditAdmin(admin);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAdmin(admin);
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Layout title="Admin Management">
      {/* Header with search and add button */}
      <div
        style={{
          ...utilityStyles.flexRow,
          ...utilityStyles.spaceBetween,
          ...utilityStyles.alignCenter,
          ...utilityStyles.marginBottom20,
        }}
      >
        <SearchBar
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search by name or email..."
        />
        <Button onClick={handleAddAdmin}>Add Admin</Button>
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '20px',
          }}
        >
          {error}
        </div>
      )}

      {/* Table */}
      <Table columns={columns} data={admins} loading={loading} />

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={totalAdmins}
        itemsPerPage={10}
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === 'add' ? 'Add New Admin' : 'Edit Admin'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          error={formErrors.name}
          required
        />
        <Input
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          error={formErrors.email}
          required
        />
        <Input
          label={modalMode === 'add' ? 'Password (optional - will be auto-generated if empty)' : 'Password (leave blank to keep current)'}
          name="password"
          type="password"
          value={formData.password}
          onChange={handleInputChange}
          error={formErrors.password}
          placeholder={modalMode === 'add' ? 'Leave empty to auto-generate' : ''}
        />
        {modalMode === 'add' && (
          <div style={{
            fontSize: '12px',
            color: '#666',
            marginTop: '-12px',
            marginBottom: '16px',
            padding: '8px 12px',
            backgroundColor: '#f0f7ff',
            borderRadius: '4px',
            borderLeft: '3px solid #007bff'
          }}>
            Login credentials will be sent to the admin's email address.
          </div>
        )}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
            Role
          </label>
          <select
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: '14px',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
            }}
          >
            <option value="membership_admin">Membership Admin</option>
            <option value="content_admin">Content Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
            />
            <span style={{ fontSize: '14px' }}>Active</span>
          </label>
        </div>
        {formErrors.submit && (
          <div style={{ color: colors.danger, fontSize: '14px', marginTop: '12px' }}>
            {formErrors.submit}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default AdminManagement;
